import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  alpha,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupIcon from "@mui/icons-material/Group";

import useAuthUser from "../../../hooks/useAuthUser";
import {
  getEmployees,
  getLocations,
} from "../../../utils/services/control/masterDataService";
import useTasksModule from "../hooks/useTasksModule";
import TasksKpis from "../components/TasksKpis";
import TasksFilters from "../components/TasksFilters";
import TasksDataGrid from "../components/TasksDataGrid";
import TaskFormDialog from "../components/TaskFormDialog";
import TaskDetailDrawer from "../components/TaskDetailDrawer";
import { MY_ROLES } from "../constants/taskConstants";

const MY_ROLE_FILTERS = [
  { key: "all",      label: "All",          icon: AssignmentIcon },
  { key: "creator",  label: "Created",      icon: PersonOutlineIcon },
  { key: "assignee", label: "Assigned",     icon: PersonIcon },
  { key: "both",     label: "Self-assigned", icon: GroupIcon },
];

// Normaliza el id del usuario actual a número entero (>0) o null.
// Si el JWT/secureStorage entrega el id como string ("89"), aquí queda 89.
const toEmployeeIdOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export default function TasksPage() {
  const currentUser = useAuthUser();
  const rawEmployeeId = currentUser?.employeeId ?? currentUser?.employee ?? null;
  const employeeId = toEmployeeIdOrNull(rawEmployeeId);

  // Telemetría útil cuando un usuario reporta "creé una tarea pero no la veo":
  // permite verificar el shape exacto del user en consola.
  useEffect(() => {
    console.log("[TasksPage] currentUser:", currentUser);
    console.log("[TasksPage] employeeId resuelto:", employeeId, "(raw:", rawEmployeeId, ")");
  }, [currentUser, rawEmployeeId, employeeId]);

  // El módulo solo expone tareas relacionadas con el usuario (creadas, asignadas
  // o auto-asignadas). El endpoint global GET /tasks queda reservado para roles
  // administrativos en futuras fases — hoy no hay vista "All".
  const view = "my";

  // ── Filtros ────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const [priorityId, setPriorityId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [myRoleFilter, setMyRoleFilter] = useState("all");

  const filters = useMemo(
    () => ({
      ...(status     ? { status }     : {}),
      ...(priorityId ? { priorityId } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(locationId ? { locationId } : {}),
    }),
    [status, priorityId, categoryId, locationId]
  );

  const clearFilters = () => {
    setSearch("");
    setStatus(null);
    setPriorityId(null);
    setCategoryId(null);
    setLocationId(null);
  };

  // ── Datos ──────────────────────────────────────────────────────────
  const {
    tasks,
    stats,
    loading,
    refresh,
    createTask,
    updateTask,
    completeTask,
    updateStatus,
    deleteTask,
    getTaskById,
  } = useTasksModule({ view, employeeId, filters });

  // ── Master data: employees + locations ─────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getEmployees({ status: 1 }), getLocations()])
      .then(([emps, locs]) => {
        if (cancelled) return;
        setEmployees(emps || []);
        setLocations(locs || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── UI state: form, drawer, snackbar ───────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [formDefaults, setFormDefaults] = useState(null);
  const [formError, setFormError] = useState(null);

  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const drawerOpen = drawerTaskId != null;

  const [snackbar, setSnackbar] = useState(null); // { severity, message }

  const showSnack = (severity, message) => setSnackbar({ severity, message });

  // ── Filtrado client-side: search + myRole ──────────────────────────
  const filteredTasks = useMemo(() => {
    let list = tasks;

    if (view === "my" && myRoleFilter !== "all") {
      list = list.filter((t) => t.myRole === myRoleFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((t) => {
        return (
          t.title?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.patientName?.toLowerCase().includes(q) ||
          t.assigneeName?.toLowerCase().includes(q)
        );
      });
    }

    return list;
  }, [tasks, view, myRoleFilter, search]);

  // ── Conteos por myRole para los toggle labels ──────────────────────
  const myRoleCounts = useMemo(() => {
    const counts = { all: tasks.length, creator: 0, assignee: 0, both: 0 };
    tasks.forEach((t) => {
      if (t.myRole && counts[t.myRole] !== undefined) counts[t.myRole]++;
    });
    return counts;
  }, [tasks]);

  // ── Handlers ───────────────────────────────────────────────────────
  const openCreate = () => {
    setFormDefaults(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (task) => {
    setFormDefaults(task);
    setFormError(null);
    setFormOpen(true);
  };

  const handleSubmit = async (data) => {
    setFormError(null);
    try {
      if (data.id) {
        const result = await updateTask(data.id, data);
        if (result?.result === 1) {
          setFormError(result.message || "Could not update task");
          return;
        }
        showSnack("success", "Task updated");
      } else {
        const result = await createTask(data);
        if (result?.result === 1) {
          setFormError(result.message || "Could not create task");
          return;
        }
        showSnack("success", "Task created");
      }
      setFormOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Unexpected error";
      setFormError(msg);
    }
  };

  const handleComplete = async (taskId) => {
    try {
      const result = await completeTask(taskId);
      if (result?.result === 1) showSnack("error", result.message);
      else showSnack("success", "Task completed");
    } catch (err) {
      showSnack("error", err?.response?.data?.message || "Could not complete");
    }
  };

  const handleCancel = async (taskId) => {
    try {
      const result = await updateStatus(taskId, "cancelled");
      if (result?.result === 1) showSnack("error", result.message);
      else showSnack("info", "Task cancelled");
    } catch (err) {
      showSnack("error", err?.response?.data?.message || "Could not cancel");
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task permanently? This action cannot be undone.")) return;
    try {
      const result = await deleteTask(taskId);
      if (result?.result === 1) {
        showSnack("error", result.message);
      } else {
        showSnack("success", "Task deleted");
        if (drawerTaskId === taskId) setDrawerTaskId(null);
      }
    } catch (err) {
      showSnack("error", err?.response?.data?.message || "Could not delete");
    }
  };

  const handleEditFromDrawer = (task) => {
    setDrawerTaskId(null);
    openEdit(task);
  };

  const handleCompleteFromDrawer = async (taskId) => {
    await handleComplete(taskId);
    setDrawerTaskId(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
      <Stack spacing={3}>
        {/* ── Header ───────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: 3,
                background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                color: "white",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
              }}
            >
              <AssignmentIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>Tasks</Typography>
              <Typography variant="body2" color="text.secondary">
                Track, assign and complete clinical tasks
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh tasks">
              <span>
                <IconButton
                  onClick={() => {
                    refresh();
                    showSnack("info", "Refreshing tasks...");
                  }}
                  disabled={loading}
                  size="medium"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                    borderRadius: 2,
                    transition: "transform 200ms ease",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
                    },
                    "&:active": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} thickness={5} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 600,
                px: 2.5,
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.35)",
              }}
            >
              New Task
            </Button>
          </Stack>
        </Stack>

        {/* ── KPIs ─────────────────────────────────────────────────── */}
        <TasksKpis stats={stats} loading={loading} />

        {/* ── View tabs: my / all ──────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              My Tasks
            </Typography>

            <ToggleButtonGroup
              value={myRoleFilter}
              exclusive
              onChange={(_, v) => v && setMyRoleFilter(v)}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  px: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px !important",
                  "&.Mui-selected": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                    borderColor: "primary.main",
                  },
                },
              }}
            >
              {MY_ROLE_FILTERS.map((f) => {
                const cfg = MY_ROLES.find((r) => r.key === f.key);
                const count = myRoleCounts[f.key] ?? 0;
                return (
                  <ToggleButton key={f.key} value={f.key}>
                    <f.icon sx={{ fontSize: 16, mr: 0.5, color: cfg?.color }} />
                    {f.label}
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        ml: 0.75,
                        height: 18,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                      }}
                    />
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Stack>

          {/* Filtros */}
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <TasksFilters
              search={search}
              status={status}
              priority={priorityId}
              category={categoryId}
              locationId={locationId}
              locations={locations}
              onSearchChange={setSearch}
              onStatusChange={setStatus}
              onPriorityChange={setPriorityId}
              onCategoryChange={setCategoryId}
              onLocationChange={setLocationId}
              onClear={clearFilters}
            />
          </Box>

          {/* DataGrid */}
          <Box sx={{ p: 2 }}>
            <TasksDataGrid
              tasks={filteredTasks}
              loading={loading}
              currentEmployeeId={employeeId}
              showMyRole={view === "my"}
              onRowClick={(row) => setDrawerTaskId(row.id)}
              onComplete={handleComplete}
              onEdit={openEdit}
              onCancel={handleCancel}
              onDelete={handleDelete}
            />
          </Box>
        </Paper>
      </Stack>

      {/* ── Form dialog ─────────────────────────────────────────────── */}
      <TaskFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        defaultValues={formDefaults}
        employees={employees}
        locations={locations}
        currentUser={currentUser}
        submitError={formError}
      />

      {/* ── Detail drawer ───────────────────────────────────────────── */}
      <TaskDetailDrawer
        open={drawerOpen}
        taskId={drawerTaskId}
        onClose={() => setDrawerTaskId(null)}
        getTaskById={getTaskById}
        currentEmployeeId={employeeId}
        onEdit={handleEditFromDrawer}
        onComplete={handleCompleteFromDrawer}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />

      {/* ── Snackbar ───────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar(null)}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
